import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Box, Grid, Stack, Typography } from '@mui/material';
import useImages from 'api/useImages';
import useTheme from 'api/useTheme';
import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips } from 'hooks/useUpload';
import { extractColorValue } from 'utils/colorUtils';
import { permissionsUtil } from 'utils/permissionsUtil';

import AcCard from 'components/AcCard/AcCard';
import AcSelect from 'components/AcSelect/AcSelect';
import AcSolidInput from 'components/AcSolidInput/AcSolidInput';
import ActionButton from 'components/ActionButton/ActionButton';

import { EToasterStatus, showToast } from '../../../utils/showToast';
import { ThemeLoader } from '../ThemeLoader';
import { validateImageRatio } from '../utils';

import CheckoutThemePreview from './CheckoutThemePreview';
import { defaultCheckoutTheme } from './defaults';
import { CheckoutThemeFormValues } from './types';

import 'style/forms.scss';


const CheckoutTheme = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isSuperAdmin = permissionsUtil.isSuperAdmin();

  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<CheckoutThemeFormValues>();
  const { publisherId } = useParams();

  const defaultValues = useMemo(() => {
    const defaults = {
      logo: defaultCheckoutTheme.logo,
      receiptLogo: defaultCheckoutTheme.receiptLogo,
      receiptHeader: defaultCheckoutTheme.receiptHeader,
      primaryColor: defaultCheckoutTheme.primaryColor,
      textColor: defaultCheckoutTheme.textColor,
      backgroundImage: defaultCheckoutTheme.backgroundImage,
      mobileBgImage: defaultCheckoutTheme.mobileBgImage,
      buttonColor: defaultCheckoutTheme.buttonColor,
      buttonTextColor: defaultCheckoutTheme.buttonTextColor,
      publisherId: publisherId || '',
      _id: ''
    };
    
    if (!values) {
      return defaults;
    }
    
    return {
      ...values,
      logo: values.logo || defaults.logo,
      receiptLogo: values.receiptLogo || defaults.receiptLogo,
      receiptHeader: values.receiptHeader || defaults.receiptHeader,
      primaryColor: extractColorValue(values.primaryColor) || defaults.primaryColor,
      textColor: extractColorValue(values.textColor) || defaults.textColor,
      backgroundImage: values.backgroundImage || defaults.backgroundImage,
      mobileBgImage: values.mobileBgImage || defaults.mobileBgImage,
      buttonColor: extractColorValue(values.buttonColor) || defaults.buttonColor,
      buttonTextColor: extractColorValue(values.buttonTextColor) || defaults.buttonTextColor,
      publisherId: values.publisherId || defaults.publisherId,
      _id: values._id || defaults._id
    };
  }, [values, publisherId]);

  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  useEffect(() => {
    setFieldValue('receiptHeader', generalTheme.data?.receiptHeader);
  }, [generalTheme.data?.receiptHeader, setFieldValue]);

  const { getCheckoutTheme: checkoutTheme } = useTheme(publisherId);

  const pictures = useImages(publisherId).getImages;
  const [showPreview, setShowPreview] = useState(false);

  const hasValues = useMemo(() => values && Object.keys(values).length > 0, [values]);

  useEffect(
    () =>
      setIsLoading(
        checkoutTheme.isLoading || !checkoutTheme.data || !hasValues
      ),
    [checkoutTheme?.isLoading, checkoutTheme?.data, hasValues]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <>
      {showPreview && (
        <CheckoutThemePreview
          logo={defaultValues.logo}
          bgImage={defaultValues.backgroundImage}
          bgMobileImage={defaultValues.mobileBgImage}
          primaryColor={defaultValues.primaryColor}
          textColor={defaultValues.textColor}
          buttonColor={defaultValues.buttonColor}
          buttonTextColor={defaultValues.buttonTextColor}
          onClose={() => setShowPreview(false)}
        />
      )}
      {pictures.isLoading ? null : (
        <Stack className="formContent" width="100%">
          <Stack direction="row" justifyContent="space-between">
            <Grid container>
              <Grid item xs={5}>
                <Typography
                  variant="h6"
                  sx={{
                    marginBottom: '12px',
                    fontFamily: 'Inter, Montserrat, serif',
                    fontWeight: '600'
                  }}
                >
                  Checkout Page
                </Typography>
                <Stack gap={3}>
                  <AcCard stackContainer={false}>
                    <Grid container rowSpacing={2} columnSpacing={1.5}>
                      {isSuperAdmin && (
                        <Grid item xs={12}>
                          <AcSelect
                            header="Receipt (General) Logo"
                            name="receiptLogo"
                            value={defaultValues.receiptLogo}
                            dataTestBoxId="receiptLogo"
                            uploadBtnTestId="uploadReceiptLogo"
                            dataTestListId="receiptLogoList"
                            optionDataTestIdPrefix="receiptLogoOption"
                            uploadConfig={{
                              onUploadSuccess: async (url: string) => {
                                await pictures.refetch();
                                setFieldValue('receiptLogo', url);
                              },
                              uploadType: EAssetType.LOGO,
                              uploadMessage: UploadsTooltips[EAssetType.LOGO]
                            }}
                            items={[
                              {
                                content: 'Default Appcharge logo',
                                key: 'https://media.appcharge.com/defaults/default-store-logo.svg',
                                value:
                                  'https://media.appcharge.com/defaults/default-store-logo.svg',
                                renderFunction: () => {
                                  return (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <img
                                        src="https://media.appcharge.com/defaults/default-store-logo.svg"
                                        alt="Default Appcharge logo"
                                        style={{
                                          maxWidth: 26
                                        }}
                                      />
                                      <Typography>
                                        Default Appcharge logo
                                      </Typography>
                                    </Stack>
                                  );
                                }
                              },
                              ...(pictures.data || [])
                                .filter((p: any) => p.type === EAssetType.LOGO)
                                .map((picture: any) => {
                                  return {
                                    content: picture.name,
                                    key: picture.url,
                                    value: picture.url,
                                    renderFunction: () => {
                                      return (
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          gap={1}
                                        >
                                          <img
                                            src={picture.url}
                                            alt={picture.name}
                                            style={{
                                              width: 26,
                                              maxHeight: 30
                                            }}
                                          />
                                          <Typography>
                                            {picture.name}
                                          </Typography>
                                        </Stack>
                                      );
                                    }
                                  };
                                })
                            ]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.logo && Boolean(errors.logo)}
                            tooltip="This will be the logo of your checkout."
                          />
                        </Grid>
                      )}
                      {isSuperAdmin && (
                        <Grid item xs={12}>
                          <AcSelect
                            header="Receipt Header"
                            name="receiptHeader"
                            value={defaultValues.receiptHeader}
                            dataTestBoxId="receiptHeader"
                            uploadBtnTestId="uploadReceiptHeader"
                            dataTestListId="receiptHeaderList"
                            optionDataTestIdPrefix="receiptHeaderOption"
                            uploadConfig={{
                              onUploadSuccess: async (url: string) => {
                                await pictures.refetch();
                                setFieldValue('receiptHeader', url);
                              },
                              uploadType: EAssetType.RECEIPT_HEADER,
                              uploadMessage:
                                UploadsTooltips[EAssetType.RECEIPT_HEADER]
                            }}
                            items={[
                              {
                                content: 'Default Appcharge logo',
                                key: 'https://media.appcharge.com/defaults/default-store-logo.svg',
                                value:
                                  'https://media.appcharge.com/defaults/default-store-logo.svg',
                                renderFunction: () => {
                                  return (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <img
                                        src="https://media.appcharge.com/defaults/default-store-logo.svg"
                                        alt="Default Appcharge logo"
                                        style={{
                                          maxWidth: 26
                                        }}
                                      />
                                      <Typography>
                                        Default Appcharge Header
                                      </Typography>
                                    </Stack>
                                  );
                                }
                              },
                              ...(pictures.data || [])
                                .filter(
                                  (p: any) =>
                                    p.type === EAssetType.RECEIPT_HEADER
                                )
                                .map((picture: any) => {
                                  return {
                                    content: picture.name,
                                    key: picture.url,
                                    value: picture.url,
                                    renderFunction: () => {
                                      return (
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          gap={1}
                                        >
                                          <img
                                            src={picture.url}
                                            alt={picture.name}
                                            style={{
                                              width: 26,
                                              maxHeight: 30
                                            }}
                                          />
                                          <Typography>
                                            {picture.name}
                                          </Typography>
                                        </Stack>
                                      );
                                    }
                                  };
                                })
                            ]}
                            onChange={(e) => {
                              handleChange(e);
                              setFieldValue('receiptHeader', e.target.value);
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.receiptHeader &&
                              Boolean(errors.receiptHeader)
                            }
                            tooltip="This will be the header of your checkout."
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <AcSelect
                          header="Logo"
                          name="logo"
                          value={defaultValues.logo}
                          dataTestBoxId="checkoutLogo"
                          uploadBtnTestId="uploadCheckoutLogo"
                          dataTestListId="checkoutLogoList"
                          optionDataTestIdPrefix="checkoutLogoOption"
                          uploadConfig={{
                            onUploadSuccess: async (url: string) => {
                              await pictures.refetch();
                              setFieldValue('checkout.logo', url);
                            },
                            uploadType: EAssetType.LOGO,
                            uploadMessage: UploadsTooltips[EAssetType.LOGO]
                          }}
                          items={[
                            {
                              content: 'Default Appcharge logo',
                              key: 'https://media.appcharge.com/defaults/default-store-logo.svg',
                              value:
                                'https://media.appcharge.com/defaults/default-store-logo.svg',
                              renderFunction: () => {
                                return (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <img
                                      src="https://media.appcharge.com/defaults/default-store-logo.svg"
                                      alt="Default Appcharge logo"
                                      style={{
                                        maxWidth: 26
                                      }}
                                    />
                                    <Typography>
                                      Default Appcharge logo
                                    </Typography>
                                  </Stack>
                                );
                              }
                            },
                            ...(pictures.data || [])
                              .filter((p: any) => p.type === EAssetType.LOGO)
                              .map((picture: any) => {
                                return {
                                  content: picture.name,
                                  key: picture.url,
                                  value: picture.url,
                                  renderFunction: () => {
                                    return (
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <img
                                          src={picture.url}
                                          alt={picture.name}
                                          style={{
                                            width: 26,
                                            maxHeight: 30
                                          }}
                                        />
                                        <Typography>{picture.name}</Typography>
                                      </Stack>
                                    );
                                  }
                                };
                              })
                          ]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.logo && Boolean(errors.logo)}
                          tooltip="This will be the logo of your checkout."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AcSolidInput
                          header="Primary Color"
                          name="primaryColor"
                          defaultValue={defaultValues.primaryColor}
                          setValue={setFieldValue}
                          onBlur={handleBlur}
                          dataTestId="checkoutPrimaryColor"
                          dataTestPopoverId="checkoutPrimaryColorPopover"
                          dataTestColorPickerId="checkoutPrimaryColorPicker"
                          tooltip="This will be the primary color of your checkout. It will appear behind the background image in case no background image is present."
                          error={
                            touched.primaryColor && Boolean(errors.primaryColor)
                          }
                          helperText={
                            touched.primaryColor
                              ? errors.primaryColor?.toString()
                              : ''
                          }
                        />
                        <Box mt={3}>
                          <AcSolidInput
                            header="Font Color"
                            name="textColor"
                            defaultValue={defaultValues.textColor}
                            setValue={setFieldValue}
                            onBlur={handleBlur}
                            dataTestId="checkoutTextColor"
                            dataTestPopoverId="checkoutTextColorPopover"
                            dataTestColorPickerId="checkoutTextColorPicker"
                            error={
                              touched.textColor && Boolean(errors.textColor)
                            }
                            helperText={
                              touched.textColor
                                ? errors.textColor?.toString()
                                : ''
                            }
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <AcSelect
                          header="Background Image"
                          name="backgroundImage"
                          value={defaultValues.backgroundImage}
                          dataTestBoxId="checkoutBackgroundImage"
                          uploadBtnTestId="uploadCheckoutBackgroundImage"
                          dataTestListId="checkoutBackgroundImageList"
                          optionDataTestIdPrefix="checkoutBackgroundImageOption"
                          onClear={() => {
                            setFieldValue('checkout.backgroundImage', '');
                          }}
                          uploadConfig={{
                            onUploadSuccess: async (url: string) => {
                              await pictures.refetch();
                              setFieldValue('checkout.backgroundImage', url);
                            },
                            uploadType: EAssetType.BG_DESKTOP,
                            validator: async (file) => {
                              const isValid = await validateImageRatio(
                                file,
                                1 / 2
                              );
                              if (!isValid) {
                                showToast({
                                  message: 'Image aspect ratio must be 1:2',
                                  status: EToasterStatus.ERROR
                                });
                              }
                              return isValid;
                            },
                            uploadMessage: UploadsTooltips.BG_CHECKOUT
                          }}
                          items={[
                            {
                              content: 'Default Appcharge background',
                              key: 'https://media.appcharge.com/defaults/background.png',
                              value:
                                'https://media.appcharge.com/defaults/background.png',
                              renderFunction: () => {
                                return (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <img
                                      src="https://media.appcharge.com/defaults/background.png"
                                      alt="Default Appcharge background"
                                      style={{
                                        width: 30,
                                        maxHeight: 30
                                      }}
                                    />
                                    <Typography>
                                      Default Appcharge background
                                    </Typography>
                                  </Stack>
                                );
                              }
                            },
                            ...(pictures.data || [])
                              .filter(
                                (p: any) => p.type === EAssetType.BG_DESKTOP
                              )
                              .map((picture: any) => {
                                return {
                                  content: picture.name,
                                  key: picture.url,
                                  value: picture.url,
                                  renderFunction: () => {
                                    return (
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <img
                                          src={picture.url}
                                          alt={picture.name}
                                          style={{
                                            width: 30,
                                            maxHeight: 30
                                          }}
                                        />
                                        <Typography>{picture.name}</Typography>
                                      </Stack>
                                    );
                                  }
                                };
                              })
                          ]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.backgroundImage &&
                            Boolean(errors.backgroundImage)
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <AcSelect
                          header="Background Image (Mobile)"
                          name="mobileBgImage"
                          displayEmpty
                          value={defaultValues.mobileBgImage}
                          dataTestBoxId="checkoutMobileBgImage"
                          uploadBtnTestId="uploadCheckoutMobileBgImage"
                          dataTestListId="checkoutMobileBgImageList"
                          optionDataTestIdPrefix="checkoutMobileBgImageOption"
                          onClear={() => {
                            setFieldValue('checkout.mobileBgImage', '');
                          }}
                          uploadConfig={{
                            onUploadSuccess: async (url: string) => {
                              await pictures.refetch();
                              setFieldValue('checkout.mobileBgImage', url);
                            },
                            uploadType: EAssetType.BG_MOBILE,
                            validator: async (file) => {
                              const isValid = await validateImageRatio(
                                file,
                                1 / 2
                              );
                              if (!isValid) {
                                showToast({
                                  message: 'Image aspect ratio must be 1:2',
                                  status: EToasterStatus.ERROR
                                });
                              }
                              return isValid;
                            },
                            uploadMessage: UploadsTooltips.BG_CHECKOUT
                          }}
                          items={[
                            {
                              content: 'No Background (Taken from Desktop)',
                              key: '',
                              value: '',
                              renderFunction: () => {
                                return (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography>
                                      No Background (Taken from Desktop)
                                    </Typography>
                                  </Stack>
                                );
                              }
                            },
                            ...(pictures.data || [])
                              .filter(
                                (p: any) => p.type === EAssetType.BG_MOBILE
                              )
                              .map((picture: any) => {
                                return {
                                  content: picture.name,
                                  key: picture.url,
                                  value: picture.url,
                                  renderFunction: () => {
                                    return (
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <img
                                          src={picture.url}
                                          alt={picture.name}
                                          style={{
                                            width: 30,
                                            maxHeight: 30
                                          }}
                                        />
                                        <Typography>{picture.name}</Typography>
                                      </Stack>
                                    );
                                  }
                                };
                              })
                          ]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.mobileBgImage &&
                            Boolean(errors.mobileBgImage)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} mt={0}>
                        <AcSolidInput
                          header="Button Color"
                          name="buttonColor"
                          defaultValue={defaultValues.buttonColor}
                          setValue={setFieldValue}
                          onBlur={handleBlur}
                          dataTestId="checkoutButtonColor"
                          dataTestPopoverId="checkoutButtonColorPopover"
                          dataTestColorPickerId="checkoutButtonColorPicker"
                          error={
                            touched.buttonColor && Boolean(errors.buttonColor)
                          }
                          helperText={
                            touched.buttonColor
                              ? errors.buttonColor?.toString()
                              : ''
                          }
                        />
                        <Box mt={3}>
                          <AcSolidInput
                            header="Button Font Color"
                            name="buttonTextColor"
                            defaultValue={defaultValues.buttonTextColor}
                            setValue={setFieldValue}
                            onBlur={handleBlur}
                            dataTestId="checkoutButtonTextColor"
                            dataTestPopoverId="checkoutButtonTextColorPopover"
                            dataTestColorPickerId="checkoutButtonTextColorPicker"
                            error={
                              touched.buttonTextColor &&
                              Boolean(errors.buttonTextColor)
                            }
                            helperText={
                              touched.textColor
                                ? errors.textColor?.toString()
                                : ''
                            }
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </AcCard>
                </Stack>
                <Box mt={4}>
                  <ActionButton
                    variant="outlined"
                    size="small"
                    onClick={() => setShowPreview(true)}
                    sx={{
                      textTransform: 'capitalize'
                    }}
                    text="Preview (Web and Mobile)"
                    dataTestId="checkoutPreviewButton"
                  />
                </Box>
              </Grid>
            </Grid>
            <Stack gap={1} mr={-3}>
              {/* <ActionButton
                    variant="outlined"
                    size="small"
                    onClick={() => setShowPreview(true)}
                    sx={{
                        textTransform: 'capitalize'
                    }}
                    text={'Preview Mobile'}
                /> */}
            </Stack>
          </Stack>
        </Stack>
      )}
    </>
  );
};

export default CheckoutTheme;
